import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import googleCalendarPlugin from '@fullcalendar/google-calendar';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import {
  EVENT_DETAIL_URL,
  HEADER_TOOLBAR,
  HEIGHT,
  INITIAL_VIEW,
} from '@/constants/calendar';
import useCalendarStore from '@/store/calendarStore';
import TableLoadingSpinner from '../../ui/table/TableLoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { useDateParams } from './calendarComponentHooks';
import EventListTable from './EventListTable';
import { useQueryYearlyCalendar } from '@/query/calendar/useQueryCalendar';
import { SELECT_TYPE } from '@/constants/calendar';
import { isMobile } from 'react-device-detect';
import '@/styles/calendar.css';

interface CalendarEventViewProps {
  id: string;
  title: string;
  start: string;
  end: string;
  location: string;
  description: any;
  backgroundColor: string;
  borderColor: string;
  allDay: boolean;
}

interface CalendarComponentProps {
  type: string;
  view: string;
  currentDate: Date;
  onDateChange: (dateInfo: any) => void;
  onCalendarRef: (ref: any) => void;
}

const CalendarComponent = ({
  type,
  view,
  currentDate,
  onDateChange,
  onCalendarRef,
}: CalendarComponentProps) => {
  const navigate = useNavigate();
  const { events, checkedEvent, isLoading } = useCalendarStore();
  const calendarRef = useRef(null);

  const { initialDate, handleDateChange } = useDateParams();

  const { data: globalYearlyEvents, isLoading: globalYearlyLoading } =
    useQueryYearlyCalendar(SELECT_TYPE[0], '', type === 'list' && isMobile);
  const { data: koreaYearlyEvents, isLoading: koreaYearlyLoading } =
    useQueryYearlyCalendar(SELECT_TYPE[1], '', type === 'list' && isMobile);

  useEffect(() => {
    if (onCalendarRef && calendarRef.current) {
      onCalendarRef(calendarRef.current);
    }
  }, [onCalendarRef]);

  useEffect(() => {
    if (calendarRef.current && currentDate) {
      const calendarApi = calendarRef.current.getApi();
      const currentCalendarDate = calendarApi.getDate();

      if (currentCalendarDate.getTime() !== currentDate.getTime()) {
        calendarApi.gotoDate(currentDate);
      }
    }
  }, [currentDate]);

  const handleCombinedDateChange = useCallback(
    dateInfo => {
      handleDateChange(dateInfo);
      onDateChange(dateInfo);
    },
    [handleDateChange, onDateChange]
  );

  const eventsList: CalendarEventViewProps[] = useMemo(() => {
    let list: CalendarEventViewProps[] = [];

    if (type === 'list' && isMobile) {
      if (globalYearlyEvents && checkedEvent.GLOBAL) {
        const formattedGlobalEvents = globalYearlyEvents.map(event => ({
          ...event,
          id: event.id,
          title: event.summary,
          start: event.start.dateTime || event.start.date,
          end: event.end.dateTime || event.end.date,
          location: event.location || '',
          backgroundColor: '#87CEEB',
          borderColor: '#87CEEB',
          allDay: Boolean(event.start.date && event.end.date),
        }));
        list = list.concat(formattedGlobalEvents);
      }

      if (koreaYearlyEvents && checkedEvent.KOREA) {
        const formattedKoreaEvents = koreaYearlyEvents.map(event => ({
          ...event,
          id: event.id,
          title: event.summary,
          start: event.start.dateTime || event.start.date,
          end: event.end.dateTime || event.end.date,
          location: event.location || '',
          backgroundColor: '#fccb16',
          borderColor: '#fccb16',
          allDay: Boolean(event.start.date && event.end.date),
        }));
        list = list.concat(formattedKoreaEvents);
      }
    } else {
      // 일반 캘린더 뷰에서는 useCalendarStore의 events를 사용
      Object.entries(checkedEvent).forEach(([key, checked]) => {
        if (checked && events[key]) {
          const formattedEventList: CalendarEventViewProps[] = events[key].map(
            event => ({
              ...event,
              id: event.id,
              title: event.summary,
              start: event.start.dateTime || event.start.date,
              end: event.end.dateTime || event.end.date,
              location: event.location || '',
              backgroundColor: event.backgroundColor,
              borderColor: event.borderColor,
              allDay: Boolean(
                type === 'calendar' || (event.start.date && event.end.date)
              ),
            })
          );

          list = list.concat(formattedEventList);
        }
      });
    }

    return list;
  }, [type, events, checkedEvent, globalYearlyEvents, koreaYearlyEvents]);

  const getViewType = useCallback(() => {
    if (type === 'list') {
      return INITIAL_VIEW[type];
    }

    if (type === 'calendar') {
      if (view === 'week') return 'timeGridWeek';
      if (view === 'month') return 'dayGridMonth';
      return 'dayGridMonth';
    }

    return INITIAL_VIEW[type] || 'dayGridMonth';
  }, [view, type]);

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const targetView = getViewType();

      if (calendarApi.view.type !== targetView) {
        calendarApi.changeView(targetView);
      }
    }
  }, [getViewType, type, view]);

  if (
    isLoading ||
    (type === 'list' && (globalYearlyLoading || koreaYearlyLoading))
  ) {
    return <TableLoadingSpinner width={'3rem'} height={'3rem'} />;
  }

  if (type === 'list' && isMobile) {
    return (
      <EventListTable
        events={eventsList}
        onEventClick={eventId =>
          navigate(`${EVENT_DETAIL_URL}?id=${eventId}`, {
            state: 'detail',
          })
        }
      />
    );
  }

  return (
    <FullCalendar
      ref={calendarRef}
      eventClick={e => {
        e.jsEvent.preventDefault();
        navigate(`${EVENT_DETAIL_URL}?id=${e.event.id}`, {
          state: 'detail',
        });
      }}
      plugins={[
        dayGridPlugin,
        timeGridPlugin,
        listPlugin,
        googleCalendarPlugin,
      ]}
      initialDate={currentDate || initialDate}
      initialView={getViewType()}
      datesSet={handleCombinedDateChange}
      events={eventsList}
      headerToolbar={HEADER_TOOLBAR[type]}
      eventDisplay={'auto'}
      eventTextColor={'#001326'}
      height={HEIGHT[type]}
      eventTimeFormat={{
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }}
      listDayFormat={{
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }}
      fixedWeekCount={false}
    />
  );
};

export default React.memo(CalendarComponent);
